import { useEffect, useState } from 'react';
import { aed, api, type PriceChange, type Product } from '../api';

/**
 * Price editing.
 *
 * The thing this screen has to make unmissable: a price write rebuilds the
 * public site. Prices render into the HTML at build time — that is what keeps
 * LCP fast and the figure indexable and consistent with the Offer markup — so
 * the new number is not visible to a customer until Cloudflare Pages finishes,
 * roughly a minute later.
 *
 * It also has to make "no confirmed rate" a first-class state rather than an
 * empty field, because a product with no real price must render "From AED ___"
 * and refuse checkout, not display a guess.
 */
export default function Prices({ deployHookConfigured }: { deployHookConfigured: boolean }) {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, PriceChange[]>>({});

  const load = () => {
    api
      .products()
      .then(setProducts)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(load, []);

  const unpriced = (products ?? []).filter((p) => !p.priceConfirmed);

  return (
    <>
      <h1>Prices</h1>
      <p className="sub">
        Saving a price rebuilds the public site. The figure lives in the built HTML, so it goes live
        about a minute after you save — not immediately.
      </p>

      {!deployHookConfigured && (
        <div className="warn">
          <strong>CF_PAGES_DEPLOY_HOOK is not set on the API.</strong> Prices will save, but the site
          will not rebuild and customers will keep seeing the old figure. Fix this before changing
          any price.
        </div>
      )}

      {unpriced.length > 0 && (
        <div className="warn">
          <strong>
            {unpriced.length} product{unpriced.length === 1 ? ' has' : 's have'} no confirmed rate.
          </strong>{' '}
          Those pages render “From AED ___” and cannot be checked out. That is deliberate — better
          than showing an invented number — but it blocks launch.
        </div>
      )}

      {error && <p className="err">{error}</p>}
      {!products && <p className="muted">Loading…</p>}

      {products && (
        <table>
          <thead>
            <tr>
              <th>Safari</th>
              <th>Price</th>
              <th>Unit</th>
              <th>Deposit</th>
              <th>Free cancellation</th>
              <th>Seats</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <Row
                key={p.sku}
                product={p}
                open={editing === p.sku}
                history={history[p.sku]}
                onToggle={() => {
                  setEditing(editing === p.sku ? null : p.sku);
                  if (!history[p.sku]) {
                    api
                      .priceHistory(p.sku)
                      .then((h) => setHistory((prev) => ({ ...prev, [p.sku]: h })))
                      .catch(() => undefined);
                  }
                }}
                onSaved={(updated, changes) => {
                  setProducts((prev) => (prev ?? []).map((x) => (x.sku === updated.sku ? updated : x)));
                  setHistory((prev) => ({ ...prev, [updated.sku]: changes }));
                }}
              />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Row({
  product,
  open,
  history,
  onToggle,
  onSaved,
}: {
  product: Product;
  open: boolean;
  history: PriceChange[] | undefined;
  onToggle: () => void;
  onSaved: (p: Product, h: PriceChange[]) => void;
}) {
  const [value, setValue] = useState(product.priceAed === null ? '' : String(product.priceAed));
  // The VIP safaris are "per car + per guest". Blank means per vehicle only,
  // which is every other product.
  const [perGuest, setPerGuest] = useState(
    product.pricePerGuestAed === null ? '' : String(product.pricePerGuestAed),
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(next: number | null, nextPerGuest: number | null) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const updated = await api.setPrice(product.sku, next, nextPerGuest, reason);
      const changes = await api.priceHistory(product.sku).catch(() => []);
      onSaved(updated, changes);
      const fired = changes[0]?.deployHook;
      setNote(
        fired === 'fired'
          ? 'Saved. Site rebuild triggered — live in about a minute.'
          : fired === 'not_configured'
            ? 'Saved, but no deploy hook is configured, so the site will NOT rebuild.'
            : `Saved, but the deploy hook reported "${fired ?? 'unknown'}". The site may not have rebuilt.`,
      );
      setReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td>
          <strong>{product.name}</strong>
          <div className="small muted">{product.sku}</div>
        </td>
        <td className="num">
          {product.priceConfirmed ? (
            aed(product.priceAed)
          ) : (
            <span className="pill pending">AED ___</span>
          )}
        </td>
        <td className="small muted">
          per {product.priceUnit}
          {product.pricePerGuestAed !== null && (
            <div className="small muted">+ {aed(product.pricePerGuestAed)} per guest</div>
          )}
        </td>
        <td className="num">{product.depositPercent}%</td>
        <td className="num">{product.freeCancellationHours}h</td>
        <td className="num">{product.maxGuestsPerVehicle}</td>
        <td>
          <button onClick={onToggle}>{open ? 'Close' : 'Edit'}</button>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={7}>
            <div className="card grid">
              <div className="row">
                <label htmlFor={`p-${product.sku}`} className="small muted">
                  New price (AED, per {product.priceUnit})
                </label>
                <input
                  id={`p-${product.sku}`}
                  type="number"
                  min={1}
                  step={1}
                  value={value}
                  onChange={(e) => setValue(e.currentTarget.value)}
                  style={{ width: '9rem' }}
                />
                <label htmlFor={`pg-${product.sku}`} className="small muted">
                  Per guest (AED, optional)
                </label>
                <input
                  id={`pg-${product.sku}`}
                  type="number"
                  min={1}
                  step={1}
                  placeholder="—"
                  value={perGuest}
                  onChange={(e) => setPerGuest(e.currentTarget.value)}
                  style={{ width: '8rem' }}
                />
                <input
                  aria-label="Reason"
                  placeholder="Reason (peak season, supplier change…)"
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  style={{ flex: 1, minWidth: '14rem' }}
                />
                <button
                  className="primary"
                  disabled={busy || value === '' || Number(value) <= 0}
                  onClick={() => void save(Number(value), perGuest === '' ? null : Number(perGuest))}
                >
                  {busy ? 'Saving…' : 'Save and rebuild site'}
                </button>
                <button
                  disabled={busy || !product.priceConfirmed}
                  onClick={() => {
                    if (
                      confirm(
                        'Clear the rate for this product?\n\n' +
                          'Its page will show “From AED ___” and online booking will be refused ' +
                          'until a new rate is set. Use this when a rate is genuinely unknown — ' +
                          'it is better than leaving a stale number up.',
                      )
                    ) {
                      setValue('');
                      setPerGuest('');
                      void save(null, null);
                    }
                  }}
                >
                  Clear rate
                </button>
              </div>

              {note && <p className={note.startsWith('Saved. Site') ? 'ok' : 'err'}>{note}</p>}
              {error && <p className="err">{error}</p>}

              <div>
                <h2>Change history</h2>
                {!history && <p className="muted small">Loading…</p>}
                {history && history.length === 0 && <p className="muted small">No changes recorded.</p>}
                {history && history.length > 0 && (
                  <table>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Who</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Rebuild</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.at}>
                          <td className="num small">{new Date(h.at).toLocaleString('en-GB')}</td>
                          <td className="small">{h.by}</td>
                          <td className="num">{aed(h.fromAed)}</td>
                          <td className="num">{aed(h.toAed)}</td>
                          <td className="small">
                            {h.deployHook === 'fired' ? (
                              <span className="ok">fired</span>
                            ) : (
                              <span className="err">{h.deployHook ?? 'unknown'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
